import { Component, DestroyRef, inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { CapitalizeFirstDirective } from 'src/app/shared/directives/capitalize-first.directive';
// Ag-Grid
import { GridOptions, GridApi, RowSelectionOptions } from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
import { combineLatest, Subscription } from 'rxjs';
import { startWith } from 'rxjs/operators';
// models
import { User } from 'src/app/shared/models/company';
import { UsersColumnDef } from './columnDefs';
import { SharedService } from 'src/app/shared/services/shared.service';
import { AddEditDeleteBtnsComponent } from 'src/app/shared/components/ag-grid/add-edit-delete-btns/add-edit-delete-btns.component';
import {
  Answers,
  Questions,
  signatureType,
} from 'src/app/shared/models/constants';
import { CapturedSignature } from 'src/app/shared/models/general';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, AgGridAngular, CapitalizeFirstDirective],
})
export class UserManagementComponent {
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  user_form: FormGroup;
  user_id: string | null = null;
  roles: string[] = ['Admin', 'User', 'Viewer'];
  digital_signature: string | null = null;
  edit_user: boolean = false;
  showPassword = false;
  showPassword2 = false;

  min_password_length: number = 3; // change to larger number later
  errors: string[] = [];
  missing_fields: string[] = [];

  //   user_icon = this.sharedService.company_info.user_icon;
  // ag-grid properties Grid
  gridApi!: GridApi;

  columnDefs = UsersColumnDef;
  defaultColDef;
  detailCellRendererParams: any;
  overlayLoadingTemplate;
  overlayNoRowsTemplate;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,};
  components: any;
  theme = agGridDarkTheme;

  isStandaloneProfile = false;

  constructor(
    private formBuilder: FormBuilder,
    public sharedService: SharedService,
    private router: Router
  ) {
    this.user_form = this.formBuilder.group({
      name_first: ['', Validators.required],
      name_last: ['', Validators.required],
      username: [''],
      password: [
        '',
        [Validators.required, Validators.minLength(this.min_password_length)],
      ],
      password2: [
        '',
        [Validators.required, Validators.minLength(this.min_password_length)],
      ],
      role: ['', Validators.required],
      position_title: [''],
      phone: [''],
      email: ['', [Validators.required, Validators.email]],
      digital_signature: [{ value: null, disabled: true }],
    });

    // Auto-generate username from first + last name (only when creating, not editing)
    this.subscriptions.add(
      combineLatest([
        this.user_form.get('name_first').valueChanges.pipe(startWith('')),
        this.user_form.get('name_last').valueChanges.pipe(startWith('')),
      ]).subscribe(([first, last]) => {
        if (!this.edit_user) {
          const generated = this.generateUsername(first || '', last || '');
          this.user_form.get('username').setValue(generated, { emitEvent: false });
        }
      })
    );
    this.gridOptions = {
      rowHeight: 30,
      rowSelection: {
        mode: 'singleRow',
        enableSelectionWithoutKeys: true,
        checkboxes: false,
      } as RowSelectionOptions,
    };
    this.components = {
      addEditDeleteBtns: AddEditDeleteBtnsComponent,
    };

    // yes_no_submit
    this.subscriptions.add(
      this.sharedService.yes_no_submit$.subscribe((response) => {
        if (
          response['answer'] === Answers.yes &&
          response['question'] === Questions.delete_user
        ) {
          this.sharedService.deleteUser(response.optional_details);
        }
      })
    );
    // user_added$
    this.subscriptions.add(
      this.sharedService.user_added$.subscribe((response) => {
        this.clearForm();
      })
    );
    // user_updated$
    this.subscriptions.add(
      this.sharedService.user_updated$.subscribe((response) => {
        this.clearForm();
      })
    );
    // signatureCaptured$
    this.subscriptions.add(
      this.sharedService.signatureCaptured$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: CapturedSignature) => {
          if (response.signature.length !== 0) {
            this.digital_signature = response.signature;
            this.user_form.patchValue({
              digital_signature: 'Signature Captured',
            });
            this.user_form.markAsDirty();
          } else {
            this.digital_signature = null;
            this.user_form.patchValue({ digital_signature: '' });
          }
        })
    );
  }
  onGridReady(params: any) {
    this.gridApi = params.api;

    [...this.sharedService.company_info.users];
  }
  ngOnInit(): void {
    this.isStandaloneProfile = this.router.url.includes('/user-management');
    this.addButtonColumn();
    
    if (this.sharedService.logged_in_user?.role === 'Developer' && !this.roles.includes('Developer')) {
      this.roles.push('Developer');
    }

    // Auto-load current user if not admin or if standalone profile
    if (!this.isAdmin() || this.isStandaloneProfile) {
      const currentUser = this.sharedService.users.find(u => u.user_id === this.sharedService.logged_in_user?.user_id);
      if (currentUser) {
        this.editUserClicked({ colValues: currentUser, rowIndex: 0 });
      }
    }
  }

  isAdmin(): boolean {
    const role = this.sharedService.logged_in_user?.role;
    return role === 'Admin' || role === 'Developer';
  }

  getUsers(): User[] {
    if (this.isAdmin()) {
      return this.sharedService.users;
    }
    return this.sharedService.users.filter(u => u.user_id === this.sharedService.logged_in_user?.user_id);
  }

  addButtonColumn() {
    const hasEditDelete = this.columnDefs.some(
      (column) => column.headerName === 'Edit / Delete'
    );
    if (!hasEditDelete) {
      this.columnDefs.push({
        // Edit / Delete
        headerName: 'Edit / Delete',
        field: 'edit_delete',
        resizable: true,
        width: 140,
        pinned: 'right',
        suppressMovable: true,
        cellRenderer: 'addEditDeleteBtns',
        cellRendererParams: {
          editClicked: this.editUserClicked.bind(this),
          deleteClicked: this.deleteUserClicked.bind(this),
        },
      });
    }
  }
  editUserClicked(selected_row: { colValues: User; rowIndex: number }) {
    this.edit_user = true;
    // console.warn(selected_row);
    this.user_id = selected_row['colValues'].user_id;
    this.user_form.patchValue({
      name_first: selected_row['colValues'].name_first,
      name_last: selected_row['colValues'].name_last,
      username: selected_row['colValues'].username,
      password: selected_row['colValues'].password,
      password2: selected_row['colValues'].password,
      role: selected_row['colValues'].role,
      position_title: selected_row['colValues'].position_title || '',
      phone: selected_row['colValues'].phone || '',
      email: selected_row['colValues'].email || '',
      digital_signature: selected_row['colValues'].signature
        ? 'Signature Captured'
        : '',
    });
    this.digital_signature = selected_row['colValues'].signature
      ? selected_row['colValues'].signature
      : null;
    this.user_form.markAsPristine();
    this.user_form.markAsUntouched();
  }
  deleteUserClicked(selected_row: { colValues: User; rowIndex: number }) {
    if (this.checkAdminCount() >= 2) {
      this.sharedService.openYesNoModal(
        Questions.delete_user,
        selected_row.colValues
      );
    } else {
      this.sharedService.openMessageModal(
        'Can not delete. Must have one Admin user.'
      );
    }
  }
  AddEditClick() {
    this.errors = [];
    this.missing_fields = [];
    
    // Check form validity and missing fields
    if (!this.user_form.valid || this.user_form.value.password !== this.user_form.value.password2) {
      this.user_form.markAllAsTouched();
      
      if (!this.user_form.get('name_first')?.valid) this.missing_fields.push('First Name');
      if (!this.user_form.get('name_last')?.valid) this.missing_fields.push('Last Name');
      if (!this.user_form.get('role')?.valid) this.missing_fields.push('Role');
      if (!this.user_form.get('email')?.valid) this.missing_fields.push('Email');
      if (!this.user_form.get('password')?.valid) {
        this.missing_fields.push('Password');
      }
      if (!this.user_form.get('password2')?.valid) {
        if (!this.missing_fields.includes('Password')) {
           this.missing_fields.push('Re-enter Password');
        }
      }
      if (this.user_form.value.password && this.user_form.value.password2 && this.user_form.value.password !== this.user_form.value.password2) {
        this.missing_fields.push('Passwords must match');
      }
      return; // Stop execution if invalid
    }

    console.warn(this.user_id);
    if (
      this.checkPasswordStandards() &&
      this.checkIfUsernameIsUnique(this.user_id)
    ) {
      let user = new User();
      if (this.user_id) {
        user.user_id = this.user_id;
      }
      user.name_first = this.user_form.get('name_first').value;
      user.name_last = this.user_form.get('name_last').value;
      user.password = this.user_form.get('password').value;
      user.role = this.user_form.get('role').value;
      user.username = this.user_form.get('username').value;
      user.position_title = this.user_form.get('position_title').value;
      user.phone = this.user_form.get('phone').value;
      user.email = this.user_form.get('email').value;
      user.signature = this.digital_signature;
      if (this.user_id) {
        this.sharedService.updateUser(user);
        this.edit_user = false;
      } else {
        this.sharedService.addUser(user);
      }
    } else {
      const singleStringWithLineBreaks: string = this.errors.join('\n');
      this.sharedService.openMessageModal(singleStringWithLineBreaks);
    }
  }
  clearForm() {
    if (!this.isAdmin()) {
      const currentUser = this.sharedService.users.find(u => u.user_id === this.sharedService.logged_in_user?.user_id);
      if (currentUser) {
        this.editUserClicked({ colValues: currentUser, rowIndex: 0 });
      }
      return;
    }
    this.user_form.reset();
  }
  checkAdminCount(): number {
    return this.sharedService.users.filter((u) => u.role === 'Admin').length;
  }
  checkPasswordStandards() {
    const password = this.user_form.get('password').value;
    if (password.length <= 8) {
      this.errors.push('Password must be at least 8 characters long.');
      return false;
    }
    if (!this.hasSpecialCharacter(password)) {
      this.errors.push('Password must contain at least one special character.');
      return false;
    }
    return true;
  }
  hasSpecialCharacter(str: string): boolean {
    const specialCharPattern = /[!@#$%^&*(),.?":{}|<>]/;
    if (!specialCharPattern.test(str)) {
      this.errors.push('Password needs to contain a special character.');
    }
    return specialCharPattern.test(str);
  }
  checkIfUsernameIsUnique(user_id: string) {
    const username = this.user_form.get('username').value;
    for (const user of this.sharedService.users) {
      if (user.username === username && user.user_id !== user_id) {
        this.errors.push('Username is already taken.');
        return false;
      }
    }
    return true;
  }

  /** Generate a unique username from first initial + last name, all lowercase. */
  private generateUsername(first: string, last: string): string {
    const initial = first.trim().charAt(0).toLowerCase();
    const surname = last.trim().toLowerCase().replace(/[^a-z]/g, '');
    if (!initial && !surname) return '';
    
    const baseUsername = `${initial}${surname}`;
    let generated = baseUsername;
    let counter = 2;
    
    // Check for duplicates and append a number if needed
    while (this.sharedService.users.some(u => u.username === generated)) {
      generated = `${baseUsername}${counter}`;
      counter++;
    }
    
    return generated;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  addDigitalSignature() {
    this.sharedService.openDigitalSignature(signatureType.find(s => s.name === 'ffl_employee').name);
  }
  editDigitalSignature() {
    this.sharedService.openDigitalSignature(signatureType.find(s => s.name === 'ffl_employee').name);
  }
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  togglePassword2Visibility() {
    this.showPassword2 = !this.showPassword2;
  }
}
